import React from 'react';
import styled from 'styled-components';
import { IconButton, LinearProgress, CircularProgress } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPause, faPlay } from '@fortawesome/free-solid-svg-icons';
import MemorySemanticUnifierBatchModel from '../../../models/MemorySemanticUnifierBatchModel';

type Props = {
  batchModel: MemorySemanticUnifierBatchModel;
  className?: string;
};

const MemorySemanticUnifierBatch: React.FC<Props> = ({ batchModel, className }) => {
  if (batchModel.total === 0) return null;
  const percent = Math.min(100, Math.max(0, Math.round(batchModel.percent)));

  return (
    <Container className={className}>
      <ProgressArea>
        <HeaderRow>
          <Label>Embeddings</Label>
          <Stats>
            {batchModel.done}/{batchModel.total} · {percent}%
            {batchModel.failed > 0 ? <Failed> · failed: {batchModel.failed}</Failed> : null}
            {batchModel.paused ? <Paused> · paused</Paused> : null}
          </Stats>
        </HeaderRow>
        <StyledLinearProgress variant="determinate" value={percent} />
      </ProgressArea>
      <Controls>
        {batchModel.running && !batchModel.paused ? (
          <SpinnerWrapper>
            <CircularProgress size={16} thickness={5} />
          </SpinnerWrapper>
        ) : null}
        <IconButton size="small" onClick={() => batchModel.pause()} disabled={!batchModel.running || batchModel.paused}>
          <FontAwesomeIcon icon={faPause} size="sm" />
        </IconButton>
        <IconButton size="small" onClick={() => batchModel.resume()} disabled={!batchModel.paused}>
          <FontAwesomeIcon icon={faPlay} size="sm" />
        </IconButton>
      </Controls>
    </Container>
  );
};

export default MemorySemanticUnifierBatch;

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
  margin-left: 12px;
`;

const ProgressArea = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 260px;
  max-width: 420px;
  gap: 6px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const Label = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
`;

const Stats = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const Failed = styled.span`
  color: #b91c1c;
`;

const Paused = styled.span`
  color: #92400e;
`;

const StyledLinearProgress = styled(LinearProgress)`
  && {
    height: 8px;
    border-radius: 4px;
    background-color: #e5e7eb;
    .MuiLinearProgress-bar {
      border-radius: 4px;
      background-color: #1976d2;
    }
  }
`;

const Controls = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const SpinnerWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
`;
